import React from "react";
import {isNullOrUndefined} from "../../Utilities/commonUtilities";
import {isInArray, partialOptionIndexInArray} from "../../Utilities/Types/arrayUtilities";
import {isArray} from "../../Utilities/Types/typeUtilities";
import {MapAdInfinitum} from "../AdInfinitum";
import {HtmlDisplay} from "../Display";
import {FieldsetLayout} from "./FieldsetLayout";
import type {FieldsetLayoutProps} from "./FieldsetLayout";
import {appendedOtherValue, createFakeEvent, generateOtherValue} from "./inputUtility";
import {TextField} from "./TextField";

import type {ListItem} from "../../Utilities/Types/listUtilities";
import type {ChangeHandler} from "../../types";

export interface FieldsetAdapterProps extends FieldsetLayoutProps {
    onChange?: ChangeHandler;
    options: ListItem[];
    value?: string;
    inputType?: string;
    setValue?: (value: string) => void;
    setError?: (error: string) => void;
}

const FieldsetAdapter: React.FC<FieldsetAdapterProps> = ({
                             disabled,
                             helpText,
                             isInline,
                             inputType,
                             legend,
                             name,
                             onChange,
                             options,
                             value: selectedValue,
                             error,
                             responseFeedback,
                             listHasTextField = [],
                         }: FieldsetAdapterProps) => {
    if (isNullOrUndefined(selectedValue)) {
        selectedValue = "";
    }
    const verifySelected = (option: string | number, value: string | string[]) => {
        if (isArray(value))
            return isInArray(value, option);
        return option === value;
    };

    const handleUpdate = (selectedValue: string, otherValue: string) => {
        const value = generateOtherValue(selectedValue, otherValue);
        onChange?.(createFakeEvent(value, `${name}`));
    };

    const otherSelectListValueIndex = partialOptionIndexInArray(listHasTextField, selectedValue.toString());
    const hasTextField = otherSelectListValueIndex > -1;

    const selectListValue = hasTextField ? listHasTextField[otherSelectListValueIndex] : selectedValue;
    const otherTextValue = hasTextField ? appendedOtherValue(selectedValue, selectListValue) : "";

    const component = (item: unknown, key: string | number): React.ReactNode => {
        const option = item as ListItem;
        const checked = verifySelected(option.value, selectListValue);
        return (
            <label aria-disabled={disabled} key={key} className={`flex-container`}>
                <input
                    aria-disabled={disabled}
                    type={inputType}
                    name={name}
                    data-testid={`${name}_${key}`}
                    id={`${name}_${key}`}
                    value={option.value}
                    checked={checked}
                    onChange={(e) => onChange?.(e, checked)}
                    readOnly={disabled}
                />
                <HtmlDisplay html={option.text} isInline/>
                {
                    hasTextField &&
                    <span className={`show-for-sr`}>, requires other text</span>
                }
            </label>
        );
    };

    return (
        <>
            <FieldsetLayout
                disabled={disabled}
                error={error}
                helpText={helpText}
                isInline={isInline}
                legend={legend}
                name={name}
                responseFeedback={responseFeedback}
            >
                <MapAdInfinitum component={component} yutes={options}/>
            </FieldsetLayout>
            {
                hasTextField &&
                <TextField
                    label={`Specify Other Text`}
                    name={`${name}_text`}
                    disabled={disabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleUpdate(selectListValue, event.target.value)}
                    value={otherTextValue}
                />
            }
        </>
    );
};

export {FieldsetAdapter};
